import { normalizeItemNotas, toQueryDateYMD } from '../utils/formatters';
import {
    resolveFechaVencimForSave,
    toFechaVencimISO,
    validarFechasFacturaVenta,
} from '../utils/facturaVentaFechas';
import { resolveFechaVencimFromApi, resolveVtoComprobComprobanteFromApi } from '../utils/facturaVentaVencim';
import { resolverPrecioListaDetalle } from '../utils/normalizarDetallesFacturaVenta';
import {
    pedidoTieneLineasConPrecioCero,
    precioListaDetallePedido,
    prepararDetallesParaCreateConPrecioCero,
} from '../utils/pedidoVentaLineas';
import { asegurarStockSoloEnRemito } from '../utils/politicaStockVenta';
import { mapLotesComponentesPayload } from '../utils/loteLineaHelpers';
import { logApiErrorDetail } from '../utils/apiError';
import { DocumentosKpis, normalizeDocumentosKpis } from '../utils/documentosKpis';
import { umDesdeDetalleTransaccion } from '../utils/umVentaDocumento';
import { normalizeUmSnapshotFacFromApi } from '../utils/umDocumentoDisplay';
import api from './api';
import clienteService from './clienteService';
import productoService from './productoService';
import transporteService from './transporteService';
import movMonedaService from './movMonedaService';

export interface FacturaVentaDetalle {
    nroIdformulario: number;
    nroItem: number;
    nroFormularioMaster?: number;
    nroItemMaster?: number;
    codigoArticulo?: string;
    descripcionArticulo?: string;
    cantidad?: number;
    precioUnitario?: number;
    precioLista?: number;
    /** Precio unitario mínimo neto del artículo (cliente; no se envía al API). */
    precioMinimo?: number | null;
    /** Precio unitario con IVA (Factura B / consumidor final). */
    precioUnitarioFinal?: number;
    descuento?: number;
    porcDesc?: number;
    subtotal?: number;
    /** Subtotal con IVA (Factura B / consumidor final). */
    subtotalFinal?: number;
    notas?: string;
    um?: string;
    /** Snapshot Fac: cantidad ingresada por el usuario. */
    cantidadUmfac?: number;
    /** Snapshot Fac: UM ingresada. */
    umfac?: string;
    /** Snapshot Fac: factor hacia UM base. */
    equivUmfac?: number;
    /** Snapshot Fac: precio unitario ingresado (en umfac). */
    precioUnitarioUmfac?: number;
    codigoMoneda?: number;
    cotizacion?: number;
    controlLoteSerie?: string | null;
    requiereVencimiento?: boolean | null;
    lotes?: import('./loteService').LoteAsignacion[];
    esKit?: boolean;
    lotesComponentes?: import('../utils/loteLineaHelpers').KitLoteComponenteLinea[];
}

export interface FacturaVentaCreateOptions {
    emitirConCaePrimero?: boolean;
    guardarSinCae?: boolean;
}

export interface FacturaVentaCreateResult {
    factura?: FacturaVenta;
    requiereCae?: boolean;
    caeObtenido?: boolean;
    facturaGrabada?: boolean;
    caeError?: string;
    caeObs?: string;
}

export interface FacturaVenta {
    nroFormulario: number;
    fechaEmision: string | Date;
    /** Fecha de vencimiento comercial (cobranza / cuenta corriente). Distinta de caeVencimiento. */
    fechaVencim?: string | Date;
    /** Plazo comercial del comprobante al emitirse (cuando el backend lo persista). */
    vtoComprob?: number | null;
    horaEmision?: string | Date;
    nroTipoFormulario?: string;
    tipoCliente?: string;
    tipoIva?: string;
    letra?: string;
    nroSuc?: number;
    nroComprobante?: number;
    nroCliente?: number;
    nombre?: string;
    direccionCliente?: string | null;
    direccionEnvio?: string | null;
    nroVendedor?: number;
    nroLista?: string;
    codigoMoneda?: number | string;
    subtotal?: number;
    descuento?: number;
    importeSinIva?: number;
    exento?: number;
    ivainscripto?: number;
    ivanoInscripto?: number;
    ivaotro?: number;
    importeTotal?: number;
    /** Saldo pendiente de cobro en cuenta corriente. */
    saldoPendiente?: number;
    asentado?: string;
    /** Descripción de TipoFormulario (Libro IVA Ventas) */
    tipoFormularioDescripcion?: string;
    /** Número de asiento en libro diario cuando el comprobante está contabilizado. */
    nroAsiento?: number | null;
    descripcion?: string;
    usuario?: string;
    cuit?: string;
    caeNro?: string;
    caeVencimiento?: string | null;
    caeResultado?: string;
    caeObs?: string;
    detalles?: FacturaVentaDetalle[];
    porcDesc?: number | null;
    porcDesc2?: number | null;
    porcDesc3?: number | null;
    tipoDesc?: string | null;
    tipoDesc2?: string | null;
    tipoDesc3?: string | null;
    importeDesc?: number | null;
    importeDesc2?: number | null;
    importeDesc3?: number | null;
    ordenCompra?: string;
    nroFormularioRemito?: number | null;
    transporte?: string;
    ubicacion?: string;
    nroUbicacionOrigen?: number;
    formaDePago?: string;
    idCompania?: string;
    /** Provincia de la dirección del cliente (Libro IVA Ventas). */
    codigoProvincia?: number | null;
    nombreProvincia?: string | null;
}

export interface FacturaVentaSearchParams {
    searchTerm?: string;
    pageNumber: number;
    pageSize: number;
    fechaDesde?: Date | string;
    fechaHasta?: Date | string;
    nroCliente?: number;
    nroSuc?: number;
    nombreCliente?: string;
    /** Letra del comprobante (ej. A, B, C) - identificación del documento */
    letra?: string;
    /** Número de comprobante - identificación del documento */
    nroComprobante?: number;
    /** ConCae | SinCae | Interno — filtro de estado fiscal (opcional) */
    estadoFactura?: string;
    idCompania?: string;
}

export interface FacturaVentaPagedResult {
    items: FacturaVenta[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

/** Subtotal del Libro IVA Ventas por provincia (sobre todo el filtro, no solo la página). */
export interface LibroIvaVentasSubtotalProvincia {
    codigoProvincia?: number | null;
    nombreProvincia?: string | null;
    cantidadComprobantes: number;
    totalImporteSinIva: number;
    totalExento: number;
    totalIvainscripto: number;
    totalIvanoInscripto: number;
    totalIvaotro: number;
    totalImporteTotal: number;
}

/** Resultado paginado del Libro IVA Ventas */
export interface LibroIvaVentasPagedResult {
    items: FacturaVenta[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    totalImporteSinIva?: number;
    totalExento?: number;
    totalIvainscripto?: number;
    totalIvanoInscripto?: number;
    totalIvaotro?: number;
    totalImporteTotal?: number;
    subtotalesPorProvincia?: LibroIvaVentasSubtotalProvincia[];
}

export interface LibroIvaVentasArcaTxtResult {
    comprobantesFileName: string;
    comprobantesContent: string;
    alicuotasFileName: string;
    alicuotasContent: string;
}

export interface FacturaVentaEnvioEmailPreview {
    emailsSugeridos: string[];
    asuntoSugerido?: string | null;
    mensajeSugerido?: string | null;
    emailEnviadoAnteriormente?: string | null;
    smtpConfigurado: boolean;
    remitenteEmail?: string | null;
    remitenteNombre?: string | null;
    nombreCliente?: string | null;
    comprobanteLabel?: string | null;
}

export interface FacturaVentaEnvioEmailRequest {
    destinatarios: string[];
    cc?: string[];
    asunto?: string;
    mensaje?: string;
}

export interface FacturaVentaEnvioEmailResult {
    enviado: boolean;
    mensaje?: string | null;
    emailEnvioRegistrado?: string | null;
}

export interface FacturaVentaPostEmisionEmailOffer {
    ofrecerEnvio: boolean;
    smtpConfigurado: boolean;
    clienteTieneEmail: boolean;
    emailsSugeridos: string[];
    nombreCliente?: string | null;
    comprobanteLabel?: string | null;
}

/** Quita relleno tipo CHAR(n) en notas de cada ítem al recibir respuestas del API. */
const withFacturaNotasTrimmed = (data: FacturaVenta): FacturaVenta => ({
    ...data,
    detalles: (data.detalles || []).map((d) => ({
        ...d,
        notas: normalizeItemNotas(d.notas),
        um: umDesdeDetalleTransaccion(d) || undefined,
        ...normalizeUmSnapshotFacFromApi(d),
    })),
});

const normalizeFacturaVentaFromApi = (raw: any): FacturaVenta => {
    const fechaVencimIso = resolveFechaVencimFromApi(raw);
    const vtoComprob = resolveVtoComprobComprobanteFromApi(raw);
    const base = withFacturaNotasTrimmed(raw as FacturaVenta);
    const nroUbicacionOrigen =
      Number(raw?.nroUbicacionOrigen ?? raw?.NroUbicacionOrigen) || undefined;
    return {
        ...base,
        ...(nroUbicacionOrigen ? { nroUbicacionOrigen } : {}),
        ...(fechaVencimIso != null ? { fechaVencim: fechaVencimIso } : {}),
        ...(vtoComprob != null ? { vtoComprob } : {}),
        idCompania: String(raw?.idCompania ?? raw?.IdCompania ?? '').trim() || undefined,
        saldoPendiente: Number(raw?.saldoPendiente ?? raw?.SaldoPendiente ?? 0) || 0,
    };
};

/** Variantes de nombre de API para nro formulario / ítem master en líneas de detalle */
export const resolveMasterNumber = (detalle: any, keys: string[]): number | undefined => {
    for (const key of keys) {
        const value = detalle?.[key];
        if (value === undefined || value === null || value === '') {
            continue;
        }
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return undefined;
};

const toPrecioItem = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const resolverPrecioListaCreateItem = (
    detalle: FacturaVentaDetalle,
    productoData?: any,
    cotizacionUSD?: number | null,
): number => {
    if (detalle.precioUnitario != null && Number.isFinite(Number(detalle.precioUnitario))) {
        return precioListaDetallePedido(detalle);
    }

    const precioProducto = productoData?.precioVenta ?? 0;
    if (precioProducto && precioProducto > 0) {
        const tipoMonedaProd = productoData?.tipoMoneda ?? productoData?.tipoMonedaArticulo ?? 0;
        if (Number(tipoMonedaProd) === 1 && cotizacionUSD && cotizacionUSD > 0) {
            return Number((precioProducto * cotizacionUSD).toFixed(2));
        }
        return Number(precioProducto);
    }
    return 0;
};

const extraerNroFormularioFactura = (data: any): number | null => {
    const candidatos = [
        data?.nroFormulario,
        data?.factura?.nroFormulario,
        data?.formulario?.nroFormulario,
        data?.data?.nroFormulario,
    ];
    for (const value of candidatos) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
};

const MASTER_FORMULARIO_KEYS = [
    'nroFormularioMaster',
    'nroformulariomaster',
    'NroFormularioMaster',
    'nro_formulario_master',
];

const MASTER_ITEM_KEYS = [
    'nroItemMaster',
    'nroitemmaster',
    'NroItemMaster',
    'nro_item_master',
];

/**
 * Resuelve el par master de una línea usando solo campos de seguimiento explícitos.
 * No usa nroItem/nroFormulario de la línea para evitar confundir ítem con master.
 */
export const resolveDetalleMasterKeys = (
    detalle: any,
    options?: { fallbackFormulario?: number; fallbackItem?: number },
): { nroFormularioMaster: number; nroItemMaster: number } | null => {
    const nroFormularioMaster = resolveMasterNumber(detalle, MASTER_FORMULARIO_KEYS);
    const nroItemMaster = resolveMasterNumber(detalle, MASTER_ITEM_KEYS);
    if (nroFormularioMaster && nroItemMaster) {
        return { nroFormularioMaster, nroItemMaster };
    }
    const fbForm = options?.fallbackFormulario;
    const fbItem = options?.fallbackItem;
    if (fbForm && fbForm > 0 && fbItem && fbItem > 0) {
        return { nroFormularioMaster: fbForm, nroItemMaster: fbItem };
    }
    return null;
};

const facturaVentaService = {
    getFacturasVenta: async (params: FacturaVentaSearchParams): Promise<FacturaVentaPagedResult> => {
        const queryParams: any = {
            PageNumber: params.pageNumber,
            PageSize: params.pageSize,
            SearchTerm: params.searchTerm || undefined
        };

        if (params.fechaDesde) {
            queryParams.FechaDesde = toQueryDateYMD(params.fechaDesde);
        }
        if (params.fechaHasta) {
            queryParams.FechaHasta = toQueryDateYMD(params.fechaHasta);
        }
        if (params.nroCliente) {
            queryParams.NroCliente = params.nroCliente;
        }
        if (params.nroSuc) {
            queryParams.NroSuc = params.nroSuc;
        }
        if (params.nombreCliente) {
            queryParams.NombreCliente = params.nombreCliente;
        }
        if (params.letra != null && params.letra !== '') {
            queryParams.Letra = params.letra.trim();
        }
        if (params.nroComprobante != null && params.nroComprobante > 0) {
            queryParams.NroComprobante = params.nroComprobante;
        }
        if (params.estadoFactura != null && params.estadoFactura !== '') {
            queryParams.EstadoFactura = params.estadoFactura;
        }

        const response = await api.get<FacturaVentaPagedResult>('/api/FacturasVenta', {
            params: queryParams
        });
        const data = response.data;
        return {
            ...data,
            items: (data.items ?? []).map((item) => normalizeFacturaVentaFromApi(item)),
        };
    },

    getFacturasVentaKpis: async (
        params: Omit<FacturaVentaSearchParams, 'pageNumber' | 'pageSize'>,
    ): Promise<DocumentosKpis> => {
        const queryParams: Record<string, unknown> = {
            SearchTerm: params.searchTerm || undefined,
            PageNumber: 1,
            PageSize: 1,
        };
        if (params.fechaDesde) queryParams.FechaDesde = toQueryDateYMD(params.fechaDesde);
        if (params.fechaHasta) queryParams.FechaHasta = toQueryDateYMD(params.fechaHasta);
        if (params.nroCliente) queryParams.NroCliente = params.nroCliente;
        if (params.nroSuc) queryParams.NroSuc = params.nroSuc;
        if (params.nombreCliente) queryParams.NombreCliente = params.nombreCliente;
        if (params.letra != null && params.letra !== '') queryParams.Letra = params.letra.trim();
        if (params.nroComprobante != null && params.nroComprobante > 0) {
            queryParams.NroComprobante = params.nroComprobante;
        }
        if (params.estadoFactura != null && params.estadoFactura !== '') {
            queryParams.EstadoFactura = params.estadoFactura;
        }
        const response = await api.get('/api/FacturasVenta/kpis', {
            params: queryParams,
        });
        return normalizeDocumentosKpis(response.data);
    },

    getFacturaVenta: async (nroFormulario: number): Promise<FacturaVenta> => {
        const response = await api.get<FacturaVenta>(`/api/FacturasVenta/${nroFormulario}`);
        return normalizeFacturaVentaFromApi(response.data);
    },

    /** Solo SUPERADMIN: borra asientos vinculados al formulario y crea uno nuevo con la lógica actual. */
    regenerarAsientoFacturaVenta: async (
        id: number
    ): Promise<{ nroAsiento: number; message?: string }> => {
        const response = await api.post<{ nroAsiento: number; message?: string }>(
            `/api/FacturasVenta/${id}/regenerar-asiento`
        );
        return response.data;
    },

    obtenerCaeArgentina: async (nroFormulario: number, idCompania?: string): Promise<FacturaVenta> => {
        const params = idCompania?.trim()
            ? `?idCompania=${encodeURIComponent(idCompania.trim())}`
            : '';
        const response = await api.post<FacturaVenta>(
            `/api/FacturasVenta/${nroFormulario}/obtener-cae-argentina${params}`
        );
        return normalizeFacturaVentaFromApi(response.data);
    },

    /**
     * Rescata una factura autorizada en ARCA que no existe en eOne (sin FECAESolicitar).
     */
    rescateArca: async (payload: {
        idWsfe: string;
        idCompania: string;
        cuitEmisor: string;
        puntoVenta: number;
        codigoComprobanteAfip: number;
        numeroComprobante: number;
        formulario: {
            idCompania?: string;
            nroSuc?: number;
            nroCliente: number;
            nombre?: string;
            cuit?: string;
            formaDePago?: string;
            nroUbicacionOrigen?: number;
            descripcion?: string;
            tipoIva?: string;
            nroVendedor?: number;
            nroLista?: string;
        };
        items: Array<{
            codigoArticulo: string;
            descripcion?: string;
            cantidad: number;
            precioUnitario: number;
            porcDesc?: number;
            um?: string;
        }>;
    }): Promise<FacturaVenta> => {
        const response = await api.post(`/api/FacturasVenta/rescate-arca`, payload);
        const data = response.data;
        const factura = data?.factura ?? data;
        return normalizeFacturaVentaFromApi(factura);
    },

    getLibroIvaVentas: async (params: FacturaVentaSearchParams): Promise<LibroIvaVentasPagedResult> => {
        const queryParams: any = {
            PageNumber: params.pageNumber,
            PageSize: params.pageSize,
            SearchTerm: params.searchTerm || undefined
        };

        if (params.fechaDesde) {
            queryParams.FechaDesde = toQueryDateYMD(params.fechaDesde);
        }
        if (params.fechaHasta) {
            queryParams.FechaHasta = toQueryDateYMD(params.fechaHasta);
        }
        if (params.nroCliente) {
            queryParams.NroCliente = params.nroCliente;
        }
        if (params.nroSuc) {
            queryParams.NroSuc = params.nroSuc;
        }
        if (params.nombreCliente) {
            queryParams.NombreCliente = params.nombreCliente;
        }
        if (params.idCompania) {
            queryParams.IdCompania = params.idCompania;
        }

        const response = await api.get<LibroIvaVentasPagedResult>('/api/ComprobantesVenta/libro-iva-ventas', {
            params: queryParams
        });
        return response.data;
    },

    exportLibroIvaVentasArcaTxt: async (params: {
        fechaDesde: Date | string;
        fechaHasta: Date | string;
        idCompania?: string;
    }): Promise<LibroIvaVentasArcaTxtResult> => {
        const response = await api.get<LibroIvaVentasArcaTxtResult>('/api/ComprobantesVenta/libro-iva-ventas/arca-txt', {
            params: {
                fechaDesde: toQueryDateYMD(params.fechaDesde),
                fechaHasta: toQueryDateYMD(params.fechaHasta),
                idCompania: params.idCompania || undefined,
            },
        });
        return response.data;
    },

    createFacturaVenta: async (facturaVenta: FacturaVenta & {
        nroSuc?: number;
        ordenCompra?: string;
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
        fechaEmision?: Date | string;
        horaEmision?: Date | string;
        detalles?: FacturaVentaDetalle[];
        nroFormularioPedido?: number;
        nroFormularioCotizacion?: number;
        nroFormularioOrdenTrabajo?: number;
        codigoDireccion?: string;
        codigoDireccionEnvio?: string;
        direccionEnvio?: string;
        codigoCiudadEnvio?: number;
        idCompania?: string;
    }, options?: FacturaVentaCreateOptions): Promise<FacturaVenta> => {
        // Validaciones de datos obligatorios
        const errors: string[] = [];

        // Validar cliente
        if (!facturaVenta.nroCliente || facturaVenta.nroCliente <= 0) {
            errors.push('nroCliente es obligatorio y debe ser mayor a 0');
        }

        // Obtener datos del cliente antes de crear el payload
        let clienteData: any = null;
        if (facturaVenta.nroCliente && facturaVenta.nroCliente > 0) {
            try {
                clienteData = await clienteService.getCliente(facturaVenta.nroCliente);
                if (!clienteData) {
                    errors.push(`No se encontró el cliente con número ${facturaVenta.nroCliente}`);
                }
            } catch (error: any) {
                const mensajeError = error?.response?.status === 404 
                    ? `No se encontró el cliente con número ${facturaVenta.nroCliente}`
                    : `Error al obtener los datos del cliente: ${error?.message || 'Error desconocido'}`;
                errors.push(mensajeError);
            }
        }

        // Validar que haya al menos un detalle
        if (!facturaVenta.detalles || facturaVenta.detalles.length === 0) {
            errors.push('Debe haber al menos un item en la factura');
        }

        // Validar cada detalle y obtener datos de productos
        const productosData: Record<string, any> = {};
        if (facturaVenta.detalles && facturaVenta.detalles.length > 0) {
            // Obtener datos completos de cada producto
            for (const detalle of facturaVenta.detalles) {
                if (detalle.codigoArticulo) {
                    try {
                        const producto = await productoService.getProducto(detalle.codigoArticulo);
                        if (!producto) {
                            errors.push(`No se encontró el producto con código "${detalle.codigoArticulo}"`);
                        } else {
                            productosData[detalle.codigoArticulo] = producto;
                        }
                    } catch (error: any) {
                        const itemIndex = facturaVenta.detalles.findIndex(d => d.codigoArticulo === detalle.codigoArticulo) + 1;
                        const mensajeError = error?.response?.status === 404
                            ? `Item ${itemIndex}: No se encontró el producto con código "${detalle.codigoArticulo}"`
                            : `Item ${itemIndex}: Error al obtener el producto "${detalle.codigoArticulo}": ${error?.message || 'Error desconocido'}`;
                        errors.push(mensajeError);
                    }
                }
            }

            facturaVenta.detalles.forEach((detalle, index) => {
                const itemNum = index + 1;
                // Validar codigoArticulo
                if (!detalle.codigoArticulo || String(detalle.codigoArticulo).trim() === '') {
                    errors.push(`Item ${itemNum}: codigoArticulo es obligatorio`);
                }
                // Validar descripcionArticulo
                if (!detalle.descripcionArticulo || String(detalle.descripcionArticulo).trim() === '') {
                    errors.push(`Item ${itemNum}: descripcion es obligatoria`);
                }
                // Validar cantidad
                if (detalle.cantidad === undefined || detalle.cantidad === null) {
                    errors.push(`Item ${itemNum}: cantidad es obligatoria`);
                } else if (typeof detalle.cantidad !== 'number' || isNaN(detalle.cantidad)) {
                    errors.push(`Item ${itemNum}: cantidad debe ser un número válido (tipo: ${typeof detalle.cantidad}, valor: ${detalle.cantidad})`);
                } else if (detalle.cantidad <= 0) {
                    errors.push(`Item ${itemNum}: cantidad debe ser mayor a 0 (valor actual: ${detalle.cantidad})`);
                }
            });
        }

        // Validar fechas
        if (!facturaVenta.fechaEmision) {
            errors.push('La Fecha de Emisión es obligatoria. Por favor, ingrese la fecha en la sección "Información de la Factura"');
        } else {
            const fechaEmisionDate = facturaVenta.fechaEmision instanceof Date 
                ? facturaVenta.fechaEmision 
                : new Date(facturaVenta.fechaEmision);
            if (isNaN(fechaEmisionDate.getTime())) {
                errors.push('La Fecha de Emisión debe ser una fecha válida');
            }
        }

        // Validar tipos de datos numéricos
        if (facturaVenta.nroCliente !== undefined && (typeof facturaVenta.nroCliente !== 'number' || isNaN(facturaVenta.nroCliente))) {
            errors.push('nroCliente debe ser un número válido');
        }

        if ((facturaVenta as any).nroSuc !== undefined && (facturaVenta as any).nroSuc !== null) {
            if (typeof (facturaVenta as any).nroSuc !== 'number' || isNaN((facturaVenta as any).nroSuc)) {
                errors.push('nroSuc debe ser un número válido');
            }
        }

        // Si hay errores, lanzarlos con mensaje claro
        if (errors.length > 0) {
            const errorMessage = errors.length === 1 
                ? errors[0]
                : `Se encontraron ${errors.length} errores:\n${errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`;
            console.error('❌ Errores de validación:', errors);
            throw new Error(errorMessage);
        }

        // Obtener la cotización USD solo si falta precio en el detalle y hay que usar el catálogo
        let cotizacionUSD: number | null = null;
        const requiereFallbackCatalogo = (facturaVenta.detalles ?? []).some(
            (d) => d.precioUnitario == null || !Number.isFinite(Number(d.precioUnitario)),
        );
        if (requiereFallbackCatalogo) {
            try {
                const cotizacion = await movMonedaService.getUltimaCotizacion();
                if (cotizacion && Number.isFinite(cotizacion) && cotizacion > 0) {
                    cotizacionUSD = cotizacion;
                }
            } catch (error) {
                console.warn('⚠️ No se pudo obtener cotización USD para fallback:', error);
            }
        }

        // El precio 0 explícito se respeta; solo se usa catálogo si el detalle no trae precio
        const requiereActualizacionPorPrecioCero = pedidoTieneLineasConPrecioCero(facturaVenta.detalles);
        const detallesParaCreate = requiereActualizacionPorPrecioCero
            ? prepararDetallesParaCreateConPrecioCero(facturaVenta.detalles ?? [])
            : (facturaVenta.detalles ?? []);

        // Mapear los detalles a la estructura de items esperada por el endpoint
        // Cantidad/UM comerciales (KG/PAQ): el stock se mueve solo al generar remito.
        await asegurarStockSoloEnRemito();
        const items = detallesParaCreate.map((detalle) => {
            const productoData = productosData[detalle.codigoArticulo || ''];
            const precioLista = resolverPrecioListaCreateItem(detalle, productoData, cotizacionUSD);
            
            // Tomar el porcentaje de descuento del detalle (porcDesc es el porcentaje, detalle.descuento es el monto)
            // SIEMPRE usar detalle.porcDesc si está definido (incluso si es 0, porque 0% es válido)
            // Solo usar el descuento del producto como fallback si porcDesc realmente no está definido
            let porcDesc: number;
            
            // Si porcDesc está definido en el detalle (incluso si es 0), usarlo directamente
            // No usar detalle.descuento porque es un monto, no un porcentaje
            if (detalle.porcDesc !== undefined && detalle.porcDesc !== null) {
                porcDesc = Number(detalle.porcDesc);
            } else {
                // Solo si porcDesc realmente no está definido, usar el descuento del producto como fallback
                porcDesc = productoData?.porc_Desc ?? productoData?.porcDesc ?? 0;
            }
                        
            // Como el precio ya está convertido a ARS, siempre se envía codigoMoneda: 0 y cotizacion: 0
            const codigoMoneda = 0;
            const cotizacion = 0;

            const nroFormularioMaster = resolveMasterNumber(detalle as any, [
                'nroFormularioMaster',
                'nroformulariomaster',
                'NroFormularioMaster',
                'nro_formulario_master',
            ]);
            const nroItemMaster = resolveMasterNumber(detalle as any, [
                'nroItemMaster',
                'nroitemmaster',
                'NroItemMaster',
                'nro_item_master',
            ]);

            return {
                codigoArticulo: String(detalle.codigoArticulo ?? '').trim(),
                descripcion: String(detalle.descripcionArticulo ?? productoData?.descripcion ?? '').trim(),
                cantidad: Number(detalle.cantidad ?? 0.01),
                precioUnitario: toPrecioItem(precioLista),
                porcDesc: Number(porcDesc),
                nroFormularioMaster,
                nroItemMaster,
                um: String(detalle.um ?? '').trim(),
                codigoMoneda: Number(codigoMoneda), // Siempre 0 (ARS) porque el precio ya está convertido
                cotizacion: Number(cotizacion), // Siempre 0 porque el precio ya está convertido
                notas: normalizeItemNotas(detalle.notas),
                lotes: detalle.lotes ?? [],
                lotesComponentes: mapLotesComponentesPayload(
                  (detalle as FacturaVentaDetalle).lotesComponentes,
                ),
            };
        });

        // Extraer nroUbicacion del campo ubicación (formato: "NOMBRE (número)")
        let nroUbicacionOrigen = 0;
        if ((facturaVenta as any).ubicacion) {
            try {
                const ubicacionStr = String((facturaVenta as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroUbicacionOrigen = Number(match[1]);
                }
            } catch (error) {
                nroUbicacionOrigen = 0;
            }
        }

        // Obtener el valor de formaEntrega del transporte seleccionado
        let formaEntrega: string | null = null;
        const transporteSeleccionado = String((facturaVenta as any).transporte ?? '').trim();
        if (transporteSeleccionado) {
            try {
                const transporteEncontrado = await transporteService.getTransporte(transporteSeleccionado);
                
                if (transporteEncontrado) {
                    const formaEntregaRaw = transporteEncontrado.formaEntrega ?? (transporteEncontrado as any).formaEntrega;
                    
                    if (formaEntregaRaw !== null && formaEntregaRaw !== undefined) {
                        formaEntrega = String(formaEntregaRaw).trim() || null;
                    } else {
                        formaEntrega = null;
                    }
                }
            } catch (error) {
                console.error('❌ Error al obtener transporte:', error);
                formaEntrega = null;
            }
        }

        // Obtener fechaEmision y fechaVencim comercial
        const fechaEmisionDate = facturaVenta.fechaEmision 
            ? (facturaVenta.fechaEmision instanceof Date ? facturaVenta.fechaEmision : new Date(facturaVenta.fechaEmision))
            : new Date();
        const fechaEmisionISO = fechaEmisionDate.toISOString();
        const fechaVencimDate = resolveFechaVencimForSave(fechaEmisionDate, facturaVenta.fechaVencim);
        const fechaVencimError = validarFechasFacturaVenta(fechaEmisionDate, fechaVencimDate);
        if (fechaVencimError) {
            errors.push(fechaVencimError);
        }
        const fechaVencimISO = toFechaVencimISO(fechaVencimDate);

        const getPorcentajeBonificacion = (...values: Array<number | string | null | undefined>) => {
            for (const value of values) {
                if (value === null || value === undefined) continue;
                const num = Number(value);
                if (Number.isFinite(num)) {
                    return Math.max(0, Math.min(100, num));
                }
            }
            return 0;
        };

        const porcDescValue = getPorcentajeBonificacion(
            (facturaVenta as any).porcDesc,
            clienteData?.porcDesc,
            (clienteData as any)?.porc_Desc
        );
        const porcDesc2Value = getPorcentajeBonificacion(
            (facturaVenta as any).porcDesc2,
            clienteData?.porcDesc2,
            (clienteData as any)?.porc_Desc2
        );
        const porcDesc3Value = getPorcentajeBonificacion(
            (facturaVenta as any).porcDesc3,
            clienteData?.porcDesc3,
            (clienteData as any)?.porc_Desc3
        );

        // Construir el objeto formulario según la estructura del endpoint
        const idCompaniaValue = String((facturaVenta as any).idCompania ?? '').trim();

        const formulario: any = {
            descripcion: String(facturaVenta.descripcion ?? '').trim(),
            nroSuc: (facturaVenta as any).nroSuc ?? 0,
            fechaEmision: fechaEmisionISO,
            horaEmision: fechaEmisionISO,
            fechaVencim: fechaVencimISO,
            nroCliente: Number(clienteData?.nroCliente ?? facturaVenta.nroCliente ?? 0),
            nroLista: String(clienteData?.nroLista ?? facturaVenta.nroLista ?? '').trim(),
            formaDePago: String((facturaVenta as any).formaDePago ?? '').trim(),
            nroUbicacionOrigen: Number(nroUbicacionOrigen),
            ordenDeCompra: String((facturaVenta as any).ordenCompra ?? '').trim(),
            codigoDireccion: String((facturaVenta as any).codigoDireccion ?? '').trim(),
            codigoDireccionEnvio: String((facturaVenta as any).codigoDireccionEnvio ?? '').trim(),
            direccionEnvio: String((facturaVenta as any).direccionEnvio ?? '').trim(),
            formaEntrega: formaEntrega ?? '',
            porcDesc: porcDescValue,
            porcDesc2: porcDesc2Value,
            porcDesc3: porcDesc3Value,
            tipoDesc: String((facturaVenta as any).tipoDesc ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P',
            tipoDesc2: String((facturaVenta as any).tipoDesc2 ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P',
            tipoDesc3: String((facturaVenta as any).tipoDesc3 ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P',
            importeDesc: Number((facturaVenta as any).importeDesc ?? 0) || 0,
            importeDesc2: Number((facturaVenta as any).importeDesc2 ?? 0) || 0,
            importeDesc3: Number((facturaVenta as any).importeDesc3 ?? 0) || 0,
            integrar: 0
        };

        if (idCompaniaValue) {
            formulario.idCompania = idCompaniaValue;
        }

        const nroVendedorRequest = Number(facturaVenta.nroVendedor ?? 0);
        if (Number.isFinite(nroVendedorRequest) && nroVendedorRequest > 0) {
            formulario.nroVendedor = nroVendedorRequest;
        }

        // Condición frente al IVA del receptor (request tiene prioridad sobre el maestro del cliente)
        const tipoIva = String(
            (facturaVenta as any).tipoIVA
                ?? facturaVenta.tipoIva
                ?? clienteData?.tipoIva
                ?? facturaVenta.tipoCliente
                ?? ''
        ).trim();
        if (tipoIva) {
            formulario.tipoIva = tipoIva;
        }

        // Receptor en comprobante: prioridad request (POS / formulario) sobre maestro (lo aplica el API)
        const nombreFactura = String(facturaVenta.nombre ?? '').trim();
        if (nombreFactura) {
            formulario.nombre = nombreFactura;
        }

        // POS y pantallas de emisión editan el documento en `cuit`; el formulario clásico usa `dni` para CF.
        const docIngresado = String(
            facturaVenta.cuit ?? (facturaVenta as any).dni ?? '',
        ).trim();
        const esConsumidorFinal = tipoIva === 'C' || tipoIva === 'CF';
        const valorCuit =
            docIngresado
            || (esConsumidorFinal
                ? String((clienteData as any)?.dni ?? (clienteData as any)?.cuit ?? '').trim()
                : String((clienteData as any)?.cuit ?? (clienteData as any)?.dni ?? '').trim());
        if (valorCuit) {
            formulario.cuit = valorCuit;
        }
        
        // Incluir nroFormularioPedido si viene informado (para marcar el pedido como facturado)
        if (facturaVenta.nroFormularioPedido !== undefined && facturaVenta.nroFormularioPedido !== null && facturaVenta.nroFormularioPedido > 0) {
            formulario.nroFormularioPedido = Number(facturaVenta.nroFormularioPedido);
        }

        // Incluir nroFormularioCotizacion si viene informado (para marcar la cotización como facturada)
        if ((facturaVenta as any).nroFormularioCotizacion !== undefined && (facturaVenta as any).nroFormularioCotizacion !== null && (facturaVenta as any).nroFormularioCotizacion > 0) {
            formulario.nroFormularioCotizacion = Number((facturaVenta as any).nroFormularioCotizacion);
        }

        // Incluir nroFormularioOrdenTrabajo si viene informado (para marcar la OT como facturada)
        if (
            facturaVenta.nroFormularioOrdenTrabajo !== undefined &&
            facturaVenta.nroFormularioOrdenTrabajo !== null &&
            facturaVenta.nroFormularioOrdenTrabajo > 0
        ) {
            formulario.nroFormularioOrdenTrabajo = Number(facturaVenta.nroFormularioOrdenTrabajo);
        }
        
        // Solo incluir codigoCiudadEnvio si tiene un valor válido
        const codigoCiudadEnvioValue = (facturaVenta as any).codigoCiudadEnvio;
        if (codigoCiudadEnvioValue !== undefined && codigoCiudadEnvioValue !== null && codigoCiudadEnvioValue !== 0) {
            formulario.codigoCiudadEnvio = Number(codigoCiudadEnvioValue);
        }

        // Preparar el payload según la especificación del endpoint
        const payload = {
            formulario,
            items,
            emitirConCaePrimero: options?.emitirConCaePrimero === true,
            guardarSinCae: options?.guardarSinCae === true,
        };

        // Usar el endpoint de facturas: /api/FacturasVenta
        const endpoint = '/api/FacturasVenta';
        
        // Log detallado para depuración
        console.log('📤 ===== INICIO PETICIÓN POST FACTURA =====');
        console.log('📤 Endpoint:', endpoint);
        console.log('📤 Base URL:', api.defaults.baseURL);
        console.log('📤 Método: POST');
        console.log('📤 Payload completo:', JSON.stringify(payload, null, 2));
        console.log('📤 URL completa será:', `${api.defaults.baseURL}${endpoint}`);
        console.log('📤 ===== FIN LOG PRE-ENVÍO =====');

        try {
            const response = await api.post<FacturaVenta | FacturaVentaCreateResult>(endpoint, payload);
            
            console.log(`✅ ===== RESPUESTA EXITOSA FACTURA =====`);
            console.log('✅ Endpoint usado:', endpoint);
            console.log('✅ Status:', response.status);
            console.log('✅ Data:', response.data);
            console.log('✅ ===== FIN RESPUESTA =====');
            
            const data = response.data as FacturaVentaCreateResult & FacturaVenta;
            if (requiereActualizacionPorPrecioCero) {
                const nroFormulario = extraerNroFormularioFactura(data);
                if (!nroFormulario) {
                    throw new Error('La factura se creó pero no se pudo obtener su número para guardar líneas con precio cero.');
                }
                await facturaVentaService.updateFacturaVenta(nroFormulario, facturaVenta);
                return facturaVentaService.getFacturaVenta(nroFormulario);
            }
            if (data && typeof data === 'object' && data.factura != null) {
                return normalizeFacturaVentaFromApi(data.factura);
            }
            return normalizeFacturaVentaFromApi(data as FacturaVenta);
        } catch (error: unknown) {
            logApiErrorDetail(error, 'POST Factura');
            throw error;
        }
    },

    updateFacturaVenta: async (id: number, facturaVenta: FacturaVenta & {
        nroSuc?: number;
        ordenCompra?: string;
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
        nroUbicacionOrigen?: number;
        formaEntrega?: string;
    }): Promise<void> => {
        // Extraer nroUbicacionOrigen de ubicación (formato: "NOMBRE (número)")
        let nroUbicacionOrigen = 0;
        if ((facturaVenta as any).ubicacion) {
            try {
                const ubicacionStr = String((facturaVenta as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroUbicacionOrigen = Number(match[1]);
                }
            } catch {
                nroUbicacionOrigen = 0;
            }
        }

        const fechaEmision = facturaVenta.fechaEmision
            ? (facturaVenta.fechaEmision instanceof Date ? facturaVenta.fechaEmision : new Date(facturaVenta.fechaEmision))
            : new Date();
        
        // Normalizar fechaEmision a medianoche (00:00:00) para evitar problemas de zona horaria
        const fechaEmisionNormalizada = new Date(fechaEmision);
        fechaEmisionNormalizada.setHours(0, 0, 0, 0);

        const fechaVencimDate = resolveFechaVencimForSave(fechaEmisionNormalizada, facturaVenta.fechaVencim);
        const fechaVencimError = validarFechasFacturaVenta(fechaEmisionNormalizada, fechaVencimDate);
        if (fechaVencimError) {
            throw new Error(fechaVencimError);
        }
        const fechaVencimNormalizada = new Date(fechaVencimDate);
        fechaVencimNormalizada.setHours(0, 0, 0, 0);
        
        const horaEmision = facturaVenta.horaEmision
            ? (facturaVenta.horaEmision instanceof Date ? facturaVenta.horaEmision : new Date(facturaVenta.horaEmision))
            : new Date();

        await asegurarStockSoloEnRemito();
        const detalles = (facturaVenta.detalles || []).map((detalle, index) => {
            const precioLista = resolverPrecioListaDetalle(detalle);
            const porcDesc = detalle.porcDesc ?? 0;
            const descuentoMonto = (precioLista * porcDesc) / 100;
            const precioUnitario = precioLista - descuentoMonto;

            const nroFormularioMaster = resolveMasterNumber(detalle as any, [
                'nroFormularioMaster',
                'nroformulariomaster',
                'NroFormularioMaster',
                'nro_formulario_master',
            ]);
            const nroItemMaster = resolveMasterNumber(detalle as any, [
                'nroItemMaster',
                'nroitemmaster',
                'NroItemMaster',
                'nro_item_master',
            ]);

            return {
                nroIdformulario: id,
                nroItem: detalle.nroItem || (index + 1),
                nroFormularioMaster,
                nroItemMaster,
                codigoArticulo: detalle.codigoArticulo || '',
                descripcionArticulo: detalle.descripcionArticulo || '',
                cantidad: detalle.cantidad ?? 0,
                precioLista: Number(precioLista),
                porcDesc: Number(porcDesc),
                precioUnitario: Number(precioUnitario),
                descuento: Number(descuentoMonto),
                subtotal: (detalle.cantidad ?? 0) * precioUnitario,
                um: String(detalle.um ?? '').trim(),
                notas: normalizeItemNotas(detalle.notas),
                lotes: (detalle as any).lotes ?? [],
                lotesComponentes: mapLotesComponentesPayload(
                  (detalle as FacturaVentaDetalle).lotesComponentes,
                ),
            };
        });
        const payload = {
            nroFormulario: id,
            fechaEmision: fechaEmisionNormalizada.toISOString(),
            fechaVencim: fechaVencimNormalizada.toISOString(),
            horaEmision: horaEmision.toISOString(),
            nroTipoFormulario: facturaVenta.nroTipoFormulario || '1',
            tipoCliente: facturaVenta.tipoCliente || 'C',
            nroCliente: facturaVenta.nroCliente ?? 0,
            nombre: facturaVenta.nombre || '',
            nroVendedor: facturaVenta.nroVendedor ?? 0,
            nroLista: facturaVenta.nroLista || '',
            codigoMoneda: Number(facturaVenta.codigoMoneda) || 0,
            formaDePago: (facturaVenta as any).formaDePago || '',
            nroUbicacionOrigen: nroUbicacionOrigen || (facturaVenta as any).nroUbicacionOrigen || 0,
            ordenDeCompra: (facturaVenta as any).ordenCompra || '',
            formaEntrega: (facturaVenta as any).formaEntrega || facturaVenta.formaDePago || '',
            porcDesc: facturaVenta.porcDesc ?? 0,
            porcDesc2: facturaVenta.porcDesc2 ?? 0,
            porcDesc3: facturaVenta.porcDesc3 ?? 0,
            tipoDesc: String((facturaVenta as any).tipoDesc ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P',
            tipoDesc2: String((facturaVenta as any).tipoDesc2 ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P',
            tipoDesc3: String((facturaVenta as any).tipoDesc3 ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P',
            importeDesc: Number((facturaVenta as any).importeDesc ?? 0) || 0,
            importeDesc2: Number((facturaVenta as any).importeDesc2 ?? 0) || 0,
            importeDesc3: Number((facturaVenta as any).importeDesc3 ?? 0) || 0,
            tipoIva: (facturaVenta as any).tipoIVA ?? facturaVenta.tipoIva ?? '',
            cuit: facturaVenta.cuit || '',
            descripcion: facturaVenta.descripcion || '',
            detalles,
        };

        await api.put(`/api/FacturasVenta/${id}`, payload);
    },

    anularFactura: async (id: number): Promise<void> => {
        await api.post(`/api/FacturasVenta/${id}/anular`);
    },

    updateFacturaVentaObservaciones: async (
        id: number,
        descripcion: string,
    ): Promise<void> => {
        const trimmed = String(descripcion ?? '').trim();
        await api.patch(`/api/FacturasVenta/${id}/observaciones`, {
            descripcion: trimmed,
        });
    },

    getEnvioEmailPreview: async (nroFormulario: number): Promise<FacturaVentaEnvioEmailPreview> => {
        const response = await api.get<FacturaVentaEnvioEmailPreview>(
            `/api/FacturasVenta/${nroFormulario}/envio-email`
        );
        return response.data;
    },

    getOfertaEnvioEmailPostEmision: async (
        nroFormulario: number
    ): Promise<FacturaVentaPostEmisionEmailOffer> => {
        const response = await api.get<FacturaVentaPostEmisionEmailOffer>(
            `/api/FacturasVenta/${nroFormulario}/envio-email/oferta-post-emision`
        );
        return response.data;
    },

    enviarFacturaPorEmail: async (
        nroFormulario: number,
        payload: FacturaVentaEnvioEmailRequest
    ): Promise<FacturaVentaEnvioEmailResult> => {
        const response = await api.post<FacturaVentaEnvioEmailResult>(
            `/api/FacturasVenta/${nroFormulario}/enviar-email`,
            payload
        );
        return response.data;
    },

    /** Envía al email del cliente sin diálogo (asunto/mensaje sugeridos). */
    enviarFacturaPorEmailAlCliente: async (
        nroFormulario: number
    ): Promise<FacturaVentaEnvioEmailResult> => {
        const response = await api.post<FacturaVentaEnvioEmailResult>(
            `/api/FacturasVenta/${nroFormulario}/enviar-email-cliente`
        );
        return response.data;
    },
};

export default facturaVentaService;
